import { invalidateAllCompanyFilterQueries } from '@Queries/index';
import { CompanySearchRequestProperty, ESelectionState, ISystemJob } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useModal } from '../../../../../models/hooks/useModal';
import { useToggle } from '../../../../../models/hooks/useToggle';
import { useDeleteCompaniesMutation, useExportCompaniesMutation } from '../../../../../queries/Company';
import { DeleteConfirmation } from '../../../../components/DeleteConfirmation';
import { ExportConfirmationModal } from '../../../../components/ExportConfirmation';
import { Modal } from '../../../../components/Modal';
import { TinyPopover } from '../../../../components/TinyPopover';
import { MergeCompaniesWizard } from '../../../../components/companies/MergeCompaniesWizard';
import { ExportIcon } from '../../../../components/svgs/icons/ExportIcon';
import { MergeIcon } from '../../../../components/svgs/icons/MergeIcon';
import { TrashIcon } from '../../../../components/svgs/icons/TrashIcon';
import { header } from '../../../../styles/colors';
import { useCompaniesSearch } from '../CompaniesContext';
import { styleSheet } from './styles';

const useActionBarStatus = () => {
	const { selectionState, selectedCompanyIds } = useCompaniesSearch();
	const isSelectingAll =
		selectionState === ESelectionState.All ||
		(selectionState === ESelectionState.Some && selectedCompanyIds.length === 0);

	const allOrSomeAreSelected = selectedCompanyIds.length > 0;
	const canDelete = allOrSomeAreSelected;
	const canMerge = selectedCompanyIds.length > 1;
	const canExport = allOrSomeAreSelected || isSelectingAll;

	return { canDelete, canExport, canMerge } as const;
};

export const CompaniesActionsBar: React.FC = observer(() => {
	const { companies, filter, selectedCompanyIds, setSelectedCompanyIds, selectionState, setSelectionState } =
		useCompaniesSearch();
	const errorMessages = useErrorMessages();
	const { canMerge, canDelete, canExport } = useActionBarStatus();
	const userSession = useUserSession();

	const [exportCompaniesPromise, setExportCompaniesPromise] = React.useState<Promise<ISystemJob>>(null);
	const [isMoreMenuOpen, , setMoreMenuOpen] = useToggle(false);

	const deleteCompaniesMutation = useDeleteCompaniesMutation({
		impersonationContext: userSession.impersonationContext,
		onSuccess: () => {
			const count = selectedCompanyIds.length;
			toast.success(`${count} compan${count > 1 ? 'ies' : 'y'} successfully deleted`);
			setSelectedCompanyIds([]);
			setSelectionState(ESelectionState.None);
		},
	});

	const exportCompaniesMutation = useExportCompaniesMutation({
		impersonationContext: userSession.impersonationContext,
		onSuccess: () => {
			toast.success('Export initiated. You will receive an email when the export is ready.');
			setSelectedCompanyIds([]);
			setSelectionState(ESelectionState.None);
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const mergeModal = useModal(false, () => {
		// noop
	}, [selectedCompanyIds, setSelectedCompanyIds]);

	const deleteModal = useModal(
		false,

		async (shouldDelete: boolean) => {
			if (!shouldDelete) {
				return;
			}
			await deleteCompaniesMutation.mutateAsync(selectedCompanyIds);
			invalidateAllCompanyFilterQueries();
		},
		[selectedCompanyIds, setSelectedCompanyIds]
	);

	const onDeleteClicked = React.useCallback(() => {
		deleteModal.setIsOpen(true)();
	}, [deleteModal]);

	const onExportCompaniesButtonClicked = () => {
		// If the user selects the select all toggle with no filters we can export all companies, else send the selected companies
		const sendAllSelectedToApi =
			selectionState === ESelectionState.All &&
			filter.criteria.length === 1 &&
			filter.criteria[0].property === CompanySearchRequestProperty.Name &&
			filter.criteria[0].value === '';

		const promise = exportCompaniesMutation.mutateAsync({
			includeContacts: false,
			includeCompanyIds: sendAllSelectedToApi ? null : selectedCompanyIds,
			selectAll: sendAllSelectedToApi,
		});
		setExportCompaniesPromise(promise);
		setMoreMenuOpen(false)();
	};

	const onExportCompaniesAndContactsButtonClicked = () => {
		const sendAllSelectedToApi =
			selectionState === ESelectionState.All &&
			filter.criteria.length === 1 &&
			filter.criteria[0].property === CompanySearchRequestProperty.Name &&
			filter.criteria[0].value === '';

		setExportCompaniesPromise(
			exportCompaniesMutation.mutateAsync({
				includeContacts: true,
				includeCompanyIds: sendAllSelectedToApi ? null : selectedCompanyIds,
				selectAll: sendAllSelectedToApi,
			})
		);
		setMoreMenuOpen(false)();
	};

	const onExportCompaniesClose = () => {
		setExportCompaniesPromise(null);
	};

	return (
		<div className={css(styleSheet.container)}>
			<button
				className={css(styleSheet.button, styleSheet.merge, !canMerge && styleSheet.disabled)}
				onClick={mergeModal.setIsOpen(true)}
			>
				<span>
					<MergeIcon />
					<span className={css(styleSheet.buttonText)}>Merge</span>
				</span>
			</button>
			<button
				className={css(styleSheet.button, styleSheet.delete, !canDelete && styleSheet.disabled)}
				onClick={onDeleteClicked}
			>
				<span>
					<TrashIcon fillColor={header} />
					<span className={css(styleSheet.buttonText)}>Delete</span>
				</span>
			</button>
			<TinyPopover
				anchor={
					<button
						className={css(styleSheet.button, styleSheet.export, !canExport && styleSheet.disabled)}
						onClick={setMoreMenuOpen(true)}
					>
						<span>
							<ExportIcon />
							<span className={css(styleSheet.buttonText)}>Export</span>
						</span>
					</button>
				}
				anchorStyles={[styleSheet.anchor]}
				isOpen={isMoreMenuOpen}
				onRequestClose={setMoreMenuOpen(false)}
				dismissOnOutsideAction={true}
			>
				<ul className={css(styleSheet.moreDropdown)}>
					<li className={css(styleSheet.moreDropdownItem)}>
						<button onClick={onExportCompaniesButtonClicked}>
							<span>Export only companies</span>
						</button>
					</li>

					<li className={css(styleSheet.moreDropdownItem)}>
						<button onClick={onExportCompaniesAndContactsButtonClicked}>
							<span>Export both companies & all contacts</span>
						</button>
					</li>
				</ul>
			</TinyPopover>
			<ExportConfirmationModal
				promise={exportCompaniesPromise}
				modalProps={{
					isOpen: !!exportCompaniesPromise,
					onRequestClose: onExportCompaniesClose,
				}}
				subTitle={
					<>
						<p>The fields we&apos;ll export include: Company Name, Company, Job Title,</p>
						<p>Address, Phone Numbers, Email Address, OwnerID, and tags.</p>
					</>
				}
			/>
			<Modal isOpen={mergeModal.isOpen} onRequestClose={mergeModal.onRequestClose}>
				<MergeCompaniesWizard
					selectedCompanies={companies.filter(x => selectedCompanyIds.includes(x.id))}
					onClose={mergeModal.onRequestClose}
				/>
			</Modal>
			<DeleteConfirmation
				bodyText='Are you sure you want to delete these companies?'
				isOpen={deleteModal.isOpen}
				onFinish={deleteModal.onRequestClose}
			/>
		</div>
	);
});
